import type { ImagingReportVersionView, ImagingReportView } from '@wodi/shared';
import { Button, Tag } from '@wodi/ui';
import { useId, useState } from 'react';

import { formatInstant } from '../patients/format.ts';
import { PRELIMINARY } from './format.ts';
import styles from './imaging.module.css';

// One imaging report as it reads everywhere (docs/plans/phase2-radiology.md §6): the
// version in force first, with its state in words, then the earlier versions on request.
// Nothing is hidden or deleted: every signed text stays readable (domain rule 6).

function VersionText({ version }: { version: ImagingReportVersionView }) {
  return (
    <>
      {version.findings === null ? null : (
        <>
          <h4 className={styles.reportHeading}>Findings</h4>
          <p className={styles.prose}>{version.findings}</p>
        </>
      )}
      <h4 className={styles.reportHeading}>Impression</h4>
      <p className={styles.prose}>{version.impression}</p>
    </>
  );
}

function signedLine(version: ImagingReportVersionView): string {
  return `Version ${String(version.version)}, signed by ${version.signedBy.displayName}, ${formatInstant(version.signedAt)}`;
}

/** The state tag: "Verified", or the preliminary warning in words. */
export function ReportStatusTag({ report }: { report: ImagingReportView }) {
  if (report.enteredInError !== null) return <Tag state="neutral">Entered in error</Tag>;
  return report.status === 'verified' ? (
    <Tag state="done">Verified</Tag>
  ) : (
    <Tag state="attention">{PRELIMINARY}</Tag>
  );
}

export function ImagingReportBody({ report }: { report: ImagingReportView }) {
  const [showEarlier, setShowEarlier] = useState(false);
  const earlierId = useId();
  const earlier = report.versions.filter((v) => v.version !== report.currentVersion);
  return (
    <div className={styles.report}>
      {report.enteredInError === null ? null : (
        <p className={styles.prose}>
          <strong>Entered in error: {report.enteredInError.reason}.</strong> This report is
          withdrawn and kept only for the history. Do not act on it.
        </p>
      )}
      <VersionText version={report.current} />
      <p className={styles.meta}>
        {signedLine(report.current)}
        {report.current.amendmentReason === null
          ? ''
          : `. Amended because: ${report.current.amendmentReason}`}
      </p>
      <p className={styles.meta}>
        {report.status === 'verified' && report.verifiedBy !== null && report.verifiedAt !== null
          ? `Verified by ${report.verifiedBy.displayName}, ${formatInstant(report.verifiedAt)}`
          : PRELIMINARY}
      </p>
      {earlier.length === 0 ? null : (
        <>
          <div>
            <Button
              dense
              variant="quiet"
              aria-expanded={showEarlier}
              aria-controls={earlierId}
              onPress={() => {
                setShowEarlier((v) => !v);
              }}
            >
              {showEarlier
                ? 'Hide earlier versions'
                : `Show earlier versions (${String(earlier.length)})`}
            </Button>
          </div>
          <ol id={earlierId} className={styles.versions} hidden={!showEarlier}>
            {earlier.map((v) => (
              <li key={v.version} className={styles.version}>
                <p className={styles.meta}>
                  {signedLine(v)}
                  {v.amendmentReason === null ? '' : `. Amended because: ${v.amendmentReason}`}
                </p>
                <VersionText version={v} />
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
